import React, { createContext, useContext, useState, useEffect } from 'react';
import { getApiUrl } from '../utils/api';

// Obtenir l'URL de l'API dynamiquement
const getApiUrlDynamic = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // Si on est sur HTTPS, utiliser une URL relative
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return '/api';
  }
  // Sinon, développement local
  return 'http://localhost:8000/api';
};

// Contexte d'authentification
const AuthContext = createContext(undefined);

// Hook pour utiliser le contexte d'authentification
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Provider d'authentification
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem('authToken') || null;
    } catch (_) {
      return null;
    }
  });

  const isAuthenticated = user !== null;

  const forceLogout = () => {
    setUser(null);
    setToken(null);
    try {
      localStorage.removeItem('authToken');
    } catch (_) {}
  };

  // Vérifier l'authentification au chargement
  const checkAuth = async () => {
    try {
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const apiUrl = getApiUrlDynamic();
      const response = await fetch(`${apiUrl}/auth/check`, {
        method: 'GET',
        credentials: 'include',
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.authenticated && data.user) {
          setUser(data.user);
        } else {
          forceLogout();
        }
      } else {
        if (response.status === 401) {
          forceLogout();
        } else {
          setUser(null);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'authentification:', error);
      forceLogout();
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction de connexion
  const login = async (email, password) => {
    try {
      const apiUrl = getApiUrlDynamic();
      const response = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          setUser(data.user);
          if (data.token) {
            try {
              localStorage.setItem('authToken', data.token);
            } catch (_) {}
            setToken(data.token);
          }
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Erreur lors de la connexion:', error);
      return false;
    }
  };

  // Fonction de déconnexion
  const logout = async () => {
    try {
      const apiUrl = getApiUrlDynamic();
      await fetch(`${apiUrl}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      setUser(null);
      setToken(null);
      try {
        localStorage.removeItem('authToken');
      } catch (_) {}
    }
  };

  // Vérifier l'authentification au montage du composant
  useEffect(() => {
    checkAuth();
  }, [token]);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuth,
    token,
    forceLogout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
